import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _extends from '@babel/runtime/helpers/esm/extends';
import React, { forwardRef, useState } from 'react';
import { useBreakpointValue, Flex, VStack, Box, useTheme, css, Stack, AspectRatio } from '@chakra-ui/react';
import { Text } from '@outdoorly/text';
import { IconButton } from '@outdoorly/button';
import { ChevronLeftIcon, ChevronRightIcon } from '@outdoorly/icons';
import propTypes from 'prop-types';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination } from 'swiper';
import { getImageLoader, getBlurUrl } from '@outdoorly/util';

var _excluded = ["content", "id", "onSlideChange", "disableMinHeight"],
    _excluded2 = ["content", "id", "as", "aspectRatio"];
SwiperCore.use([Navigation, Pagination]);
var SLIDE_HEIGHTS = {
  HIGHLIGHT_LARGE: 'sm',
  HIGHLIGHT_SMALL: '2xs'
};
var LARGE_LOGO_HEIGHTS = {
  HIGHLIGHT_LARGE: '44',
  HIGHLIGHT_SMALL: '20'
};
var SLIDE_WIDTHS = {
  HIGHLIGHT_LARGE: '3xl',
  HIGHLIGHT_SMALL: 'lg'
};
var Slide = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _actions$, _actions$2;

  var color = _ref.color,
      actions = _ref.actions,
      breadcrumbText = _ref.breadcrumbText,
      bigBackgroundImage = _ref.backgroundImage,
      mobileImage = _ref.mobileImage,
      largeLogo = _ref.largeLogo,
      smallLogo = _ref.smallLogo,
      headlineText = _ref.headlineText,
      title = _ref.title,
      subheaderText = _ref.subheaderText,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'HIGHLIGHT_LARGE' : _ref$type,
      _ref$containerProps = _ref.containerProps,
      containerProps = _ref$containerProps === void 0 ? {} : _ref$containerProps;
  var headerSize = useBreakpointValue(['2xl', '3xl', '4xl']) || 'xl';
  var backgroundImage = useBreakpointValue({
    base: mobileImage,
    md: bigBackgroundImage
  }) || bigBackgroundImage;
  var hasContent = !!(breadcrumbText || largeLogo || smallLogo || headlineText || title || subheaderText);
  var href = actions === null || actions === void 0 ? void 0 : (_actions$ = actions[0]) === null || _actions$ === void 0 ? void 0 : _actions$.href;
  var linkText = actions === null || actions === void 0 ? void 0 : (_actions$2 = actions[0]) === null || _actions$2 === void 0 ? void 0 : _actions$2.text;
  var content = /*#__PURE__*/React.createElement(Flex, _extends({
    ref: ref,
    as: "a",
    flex: 1,
    position: "relative",
    justifyContent: "center",
    align: "center",
    minHeight: SLIDE_HEIGHTS[type],
    maxHeight: SLIDE_HEIGHTS[type],
    role: "group",
    backgroundColor: "blue.800",
    _hover: {
      cursor: href ? 'pointer' : undefined
    },
    "aria-label": "".concat(linkText ? "".concat(linkText, ": ") : '').concat(title || headlineText || subheaderText)
  }, containerProps), /*#__PURE__*/React.createElement(VStack, {
    spacing: 6,
    paddingX: [8, 8, 16, 16],
    paddingY: [10, 12],
    w: "full",
    _after: {
      content: href ? "\"\"" : undefined,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgGradient: 'linear(to-t, rgba(0,0,0,.5), rgba(0,0,0,.3))',
      opacity: 0,
      _groupHover: {
        opacity: 1
      },
      transition: 'opacity .3s ease-in-out'
    }
  }, backgroundImage && /*#__PURE__*/React.createElement(Box, {
    filter: hasContent ? 'brightness(80%)' : undefined,
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 0,
    left: 0
  }, /*#__PURE__*/React.createElement(NextImage, {
    loader: getImageLoader(backgroundImage),
    src: backgroundImage,
    layout: "fill",
    placeholder: "blur",
    objectFit: "cover",
    blurDataURL: getBlurUrl(backgroundImage),
    quality: 100,
    className: "smooth-filter",
    alt: "Highlight background",
    sizes: "(min-width: 62em) 600px, 100vw"
  })), breadcrumbText && /*#__PURE__*/React.createElement(Text, {
    color: color,
    size: "lg",
    align: "center"
  }, breadcrumbText), headlineText && /*#__PURE__*/React.createElement(Text, {
    as: "h3",
    color: color,
    size: "xl",
    align: "center"
  }, headlineText), smallLogo && /*#__PURE__*/React.createElement(Box, {
    h: 10,
    position: "relative"
  }, /*#__PURE__*/React.createElement(NextImage, {
    loader: getImageLoader(smallLogo),
    src: smallLogo,
    layout: "fill",
    objectFit: "contain"
  })), largeLogo && /*#__PURE__*/React.createElement(Box, {
    h: LARGE_LOGO_HEIGHTS[type],
    position: "relative",
    w: "full",
    maxW: "300px"
  }, /*#__PURE__*/React.createElement(NextImage, {
    loader: getImageLoader(largeLogo),
    src: largeLogo,
    layout: "fill",
    objectFit: "contain"
  })), title && /*#__PURE__*/React.createElement(Text, {
    as: "h1",
    color: color,
    size: headerSize,
    fontWeight: "700",
    align: "center"
  }, title), subheaderText && /*#__PURE__*/React.createElement(Text, {
    as: "h2",
    color: color,
    size: "lg",
    align: "center"
  }, subheaderText)), linkText && /*#__PURE__*/React.createElement(Text, {
    as: "span",
    position: "absolute",
    bottom: "20px",
    color: "scheme.textPrimaryInverse",
    fontWeight: "500",
    size: "lg",
    opacity: 0,
    _groupHover: {
      bottom: '40px',
      opacity: 1
    },
    transition: "all .3s ease-in-out",
    transitionProperty: "bottom, opacity"
  }, linkText));

  if (href) {
    return /*#__PURE__*/React.createElement(NextLink, {
      href: href,
      passHref: true
    }, content);
  }

  return content;
});
Slide.propTypes = {
  containerProps: propTypes.shape(),
  actions: propTypes.arrayOf(propTypes.shape({
    text: propTypes.string,
    href: propTypes.string
  })),
  backgroundColor: propTypes.string,
  backgroundImage: propTypes.string,
  mobileImage: propTypes.string,
  breadcrumbText: propTypes.string,
  color: propTypes.string,
  headlineText: propTypes.string,
  largeLogo: propTypes.string,
  smallLogo: propTypes.string,
  subheaderText: propTypes.string,
  title: propTypes.string,
  type: propTypes.oneOf(['HIGHLIGHT_LARGE', 'HIGHLIGHT_SMALL'])
};

if (process.env.NODE_ENV !== 'production') {
  Slide.displayName = 'Slide';
}

var Description = function Description(_ref2) {
  var title = _ref2.title,
      text = _ref2.text;
  if (!(title !== null && title !== void 0 && title.length) && !(text !== null && text !== void 0 && text.length)) return null;
  return /*#__PURE__*/React.createElement(Stack, {
    py: 2,
    spacing: "xl"
  }, title && /*#__PURE__*/React.createElement(Text, {
    fontWeight: "500",
    size: "lg"
  }, title), text && /*#__PURE__*/React.createElement(Text, null, text));
};

Description.propTypes = {
  text: propTypes.string,
  title: propTypes.string
}; // eslint-disable-next-line react/display-name

var HighlightBase = /*#__PURE__*/forwardRef(function (_ref3, ref) {
  var content = _ref3.content,
      _ref3$id = _ref3.id,
      id = _ref3$id === void 0 ? 'highlight' : _ref3$id,
      _ref3$onSlideChange = _ref3.onSlideChange,
      onSlideChange = _ref3$onSlideChange === void 0 ? function () {
    return null;
  } : _ref3$onSlideChange,
      _ref3$disableMinHeigh = _ref3.disableMinHeight,
      disableMinHeight = _ref3$disableMinHeigh === void 0 ? false : _ref3$disableMinHeigh,
      props = _objectWithoutProperties(_ref3, _excluded);

  var slideProps = {};
  if (disableMinHeight) slideProps.minHeight = 0;

  if (content.length === 1) {
    return /*#__PURE__*/React.createElement(Slide, _extends({}, content[0], {
      color: "scheme.textPrimaryInverse",
      containerProps: _objectSpread(_objectSpread({
        borderRadius: 'xl',
        overflow: 'hidden'
      }, slideProps), props)
    }));
  }

  return /*#__PURE__*/React.createElement(Swiper, _extends({
    ref: ref,
    loop: true,
    navigation: {
      nextEl: "#swiper-next-button-".concat(id),
      prevEl: "#swiper-prev-button-".concat(id)
    },
    pagination: {
      clickable: true,
      renderBullet: function renderBullet(index, className) {
        return "\n              <div style=\"width: 3rem; height: 0.25rem; border-radius: 0.125rem; --swiper-pagination-color: white\" class=\"".concat(className, "\"></div>\n            ");
      }
    },
    slidesPerView: 1,
    onSlideChangeTransitionEnd: function onSlideChangeTransitionEnd(swiper) {
      return onSlideChange(swiper.realIndex);
    }
  }, props), content.map(function (slide, index) {
    return /*#__PURE__*/React.createElement(SwiperSlide, {
      key: index,
      tag: Flex,
      justify: "center"
    }, /*#__PURE__*/React.createElement(Slide, _extends({}, slide, {
      color: "scheme.textPrimaryInverse",
      containerProps: _objectSpread({
        paddingX: 24
      }, slideProps)
    })));
  }), /*#__PURE__*/React.createElement(IconButton, {
    isRound: true,
    paddingX: 3,
    icon: /*#__PURE__*/React.createElement(ChevronLeftIcon, null),
    variant: "solid",
    position: "absolute",
    top: "50%",
    left: 0,
    transform: "translate(100%, -50%)",
    zIndex: "10",
    id: "swiper-prev-button-".concat(id),
    "aria-label": "Previous",
    sx: {
      display: ['none', 'none', 'flex']
    }
  }), /*#__PURE__*/React.createElement(IconButton, {
    isRound: true,
    paddingX: 3,
    icon: /*#__PURE__*/React.createElement(ChevronRightIcon, null),
    variant: "solid",
    position: "absolute",
    top: "50%",
    right: 0,
    transform: "translate(-100%, -50%)",
    zIndex: "10",
    id: "swiper-next-button-".concat(id),
    "aria-label": "Next",
    sx: {
      display: ['none', 'none', 'flex']
    }
  }));
});
HighlightBase.propTypes = {
  content: propTypes.shape({
    length: propTypes.number,
    map: propTypes.func
  }),
  disableMinHeight: propTypes.bool,
  id: propTypes.string,
  onSlideChange: propTypes.func
};
var Highlight = /*#__PURE__*/forwardRef(function (_ref4, ref) {
  var _content$currentSlide, _content$currentSlide2;

  var content = _ref4.content,
      _ref4$id = _ref4.id,
      id = _ref4$id === void 0 ? 'highlight' : _ref4$id,
      as = _ref4.as,
      aspectRatio = _ref4.aspectRatio,
      props = _objectWithoutProperties(_ref4, _excluded2);

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      currentSlide = _useState2[0],
      setCurrentSlide = _useState2[1];

  var theme = useTheme();
  var maxWidth = css({
    maxWidth: SLIDE_WIDTHS[content[0].type]
  })(theme);
  return /*#__PURE__*/React.createElement(Stack, _extends({
    position: "relative",
    flex: 1,
    as: as,
    w: "full",
    sx: {
      maxWidth: aspectRatio ? undefined : "min(100%, ".concat(maxWidth.maxWidth, ")")
    }
  }, props), aspectRatio ? /*#__PURE__*/React.createElement(AspectRatio, {
    borderRadius: "xl",
    overflow: "hidden",
    maxW: "full",
    w: "full",
    ratio: aspectRatio
  }, /*#__PURE__*/React.createElement(HighlightBase, {
    content: content,
    id: id,
    onSlideChange: setCurrentSlide,
    ref: ref,
    maxW: "full",
    w: "full",
    disableMinHeight: true,
    position: "absolute"
  })) : /*#__PURE__*/React.createElement(Box, {
    borderRadius: "xl",
    overflow: "hidden",
    maxW: "full",
    w: "full"
  }, /*#__PURE__*/React.createElement(HighlightBase, {
    content: content,
    id: id,
    onSlideChange: setCurrentSlide,
    ref: ref,
    maxW: "full"
  })), /*#__PURE__*/React.createElement(Description, {
    title: (_content$currentSlide = content[currentSlide]) === null || _content$currentSlide === void 0 ? void 0 : _content$currentSlide.descriptionTitle,
    text: (_content$currentSlide2 = content[currentSlide]) === null || _content$currentSlide2 === void 0 ? void 0 : _content$currentSlide2.descriptionText
  }));
});

if (process.env.NODE_ENV !== 'production') {
  Highlight.displayName = 'Highlight';
}

Highlight.propTypes = {
  content: propTypes.arrayOf(propTypes.shape(Slide.propTypes)),
  type: propTypes.oneOf(['HIGHLIGHT_LARGE', 'HIGHLIGHT_SMALL']),
  aspectRatio: propTypes.number,
  id: propTypes.string,
  as: propTypes.any
};

export { Highlight, HighlightBase, Slide };
